import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AMBIENT_EVENTS, FEED_SCRIPT, LIVE_SCENARIOS } from "@/lib/liveOps";
import {
  CaseBoard, DemoHeader, DemoSummary, DetailColumn, EventStream, LiveDisclaimer,
  MetricsStrip, StepTracker, SystemsStrip, TimelinePanel, TRACKER_STEPS,
} from "@/components/liveops/LiveOpsPanels";
import { IntelligencePanel } from "@/components/liveops/IntelligencePanel";
import { PriorityQueue } from "@/components/liveops/PriorityQueue";
import { computeQual } from "@/lib/qualification";
import { computeQueue } from "@/lib/opsIntel";

const PHASE_CHIP = { event: 0, state: 1, policy: 2, decision: 2, action: 3, response: 4, update: 4, outcome: 4 };
const DEMO_SEQUENCE = ["ftd", "compliance", "retention", "suppressed", "organic"];
const INITIAL_METRICS = { assistedRevenue: 0, assistedCount: 0, kyc: 0, reengaged: 0, escalations: 0, organic: 0, suppressed: 0 };
const INITIAL_DEMO_STATS = { escalations: 0, suppressed: 0, assistedRevenue: 0, reengaged: 0, organic: 0, automated: 0 };

export default function LiveOps() {
  const [scenarioId, setScenarioId] = useState("ftd");
  const [step, setStep] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [stream, setStream] = useState([]);
  const [eventsToday, setEventsToday] = useState(1284);
  const [sla, setSla] = useState(null);
  const [breach, setBreach] = useState(false);
  const [metrics, setMetrics] = useState(INITIAL_METRICS);
  const [progressed, setProgressed] = useState(new Set());
  const [intelSpotlight, setIntelSpotlight] = useState(false);
  const [demoActive, setDemoActive] = useState(false);
  const [demoPaused, setDemoPaused] = useState(false);
  const [demoSeqIdx, setDemoSeqIdx] = useState(0);
  const [demoDone, setDemoDone] = useState(false);
  const [demoEvents, setDemoEvents] = useState(0);
  const demoActiveRef = useRef(false);
  const demoApplied = useRef(new Set());
  const [demoStats, setDemoStats] = useState(INITIAL_DEMO_STATS);
  const ambientIdx = useRef(0);
  const feedIdx = useRef(0);
  const appliedMetrics = useRef(new Set());
  const pushedSteps = useRef(new Set());
  const pushedBreach = useRef(new Set());

  const sc = useMemo(() => LIVE_SCENARIOS.find((s) => s.id === scenarioId), [scenarioId]);
  const timeline = useMemo(() => {
    const base = sc.timeline.slice(0, step + 1);
    if (sc.slaBreach && breach) return [...base, ...sc.response];
    return base;
  }, [sc, step, breach]);

  const phases = useMemo(() => new Set(timeline.map((e) => e.phase || "response")), [timeline]);
  const responseEvents = useMemo(() => timeline.filter((e) => e.phase === "response"), [timeline]);
  const delivered = timeline.some((e) => e.text.toLowerCase().includes("delivered"));
  const opened = timeline.some((e) => e.text.toLowerCase().includes("opened"));
  const maxChip = timeline.length === 0 ? -1 : Math.max(...timeline.map((e) => PHASE_CHIP[e.phase || "response"] ?? 0));
  const slaTask = sc.action.kind === "human" ? sc.action.task : null;
  const trackerIdx = TRACKER_STEPS.reduce((acc, [p], i) => (phases.has(p) ? i : acc), -1);
  const hasOutcome = phases.has("outcome");
  const hasAction = phases.has("action");

  useEffect(() => { demoActiveRef.current = demoActive; }, [demoActive]);

  const [searchParams] = useSearchParams();
  const autorunRef = useRef(false);
  useEffect(() => {
    if (searchParams.get("autorun") === "1" && !autorunRef.current) {
      autorunRef.current = true;
      runLiveDemo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const caseParam = searchParams.get("case");
  useEffect(() => {
    if (caseParam && LIVE_SCENARIOS.some((s) => s.id === caseParam) && caseParam !== scenarioId) {
      selectCase(caseParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseParam]);

  const replayParam = searchParams.get("replay");
  useEffect(() => {
    if (!replayParam || !caseParam || !LIVE_SCENARIOS.some((s) => s.id === caseParam)) return;
    if (caseParam !== scenarioId) {
      selectCase(caseParam);
    } else {
      setStep(-1);
      setPlaying(true);
      setSla(slaTask ? slaTask.slaMinutes * 60 : null);
      setBreach(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayParam]);

  const fromParam = searchParams.get("from");
  useEffect(() => {
    if (fromParam !== "impact") return;
    const t1 = setTimeout(() => {
      document.querySelector('[data-testid="intelligence-panel"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      setIntelSpotlight(true);
    }, 700);
    const t2 = setTimeout(() => setIntelSpotlight(false), 3600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromParam, caseParam]);

  useEffect(() => {
    const delta = {
      assistedFtdValue: demoStats.assistedRevenue,
      assistedFtdCount: demoStats.assistedRevenue > 0 ? 1 : 0,
      reengaged: demoStats.reengaged,
      escalations: demoStats.escalations,
      governance: demoStats.suppressed,
      organicValue: demoStats.organic * 3000,
      organicCount: demoStats.organic,
    };
    localStorage.setItem("onet_roi_delta", JSON.stringify(delta));
    window.dispatchEvent(new Event("onet-roi-delta"));
  }, [demoStats]);

  const runLiveDemo = () => {
    feedIdx.current = 0;
    appliedMetrics.current = new Set();
    demoApplied.current = new Set();
    pushedSteps.current = new Set();
    pushedBreach.current = new Set();
    setDemoStats(INITIAL_DEMO_STATS);
    setMetrics(INITIAL_METRICS);
    setProgressed(new Set());
    setStream([]);
    setBreach(false);
    setDemoDone(false);
    setDemoEvents(0);
    setDemoSeqIdx(0);
    setDemoActive(true);
    setDemoPaused(false);
    setScenarioId(DEMO_SEQUENCE[0]);
    if (scenarioId === DEMO_SEQUENCE[0]) {
      setStep(-1);
      setPlaying(true);
      setSla(null);
    }
  };

  const nextEvent = () => {
    if (!demoActive || !demoPaused) return;
    if (step < sc.timeline.length - 1) { setStep(step + 1); return; }
    if (sc.slaBreach && !breach) { setSla(13); return; }
    if (demoSeqIdx < DEMO_SEQUENCE.length - 1) {
      setDemoSeqIdx(demoSeqIdx + 1);
      setScenarioId(DEMO_SEQUENCE[demoSeqIdx + 1]);
    } else {
      setDemoActive(false);
      setDemoDone(true);
    }
  };

  const selectCase = (id) => {
    if (demoActive || demoDone) { setDemoActive(false); setDemoDone(false); }
    setScenarioId(id);
  };

  const applyEffect = (eff) => {
    if (!eff) return;
    if (eff.caseId) setProgressed((prev) => new Set(prev).add(eff.caseId));
    if (!eff.metric) return;
    const key = `${eff.caseId}:${eff.metric}`;
    if (appliedMetrics.current.has(key)) return;
    appliedMetrics.current.add(key);
    if (demoActiveRef.current) {
      setDemoStats((d) => {
        const n = { ...d };
        if (eff.metric === "assistedFtd") n.assistedRevenue += eff.amount || 0;
        if (eff.metric === "reengaged") n.reengaged += 1;
        if (eff.metric === "escalation") n.escalations += 1;
        if (eff.metric === "organic") n.organic += 1;
        if (eff.metric === "suppressed") n.suppressed += 1;
        return n;
      });
    }
    setMetrics((m) => {
      const next = { ...m };
      if (eff.metric === "assistedFtd") { next.assistedRevenue += eff.amount || 0; next.assistedCount += 1; }
      if (eff.metric === "kyc") next.kyc += 1;
      if (eff.metric === "reengaged") next.reengaged += 1;
      if (eff.metric === "escalation") next.escalations += 1;
      if (eff.metric === "organic") next.organic += 1;
      if (eff.metric === "suppressed") next.suppressed += 1;
      return next;
    });
  };

  useEffect(() => {
    setStep(-1);
    setPlaying(true);
    setSla(slaTask ? slaTask.slaMinutes * 60 : null);
    setBreach(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId]);

  useEffect(() => {
    if (demoActive && demoPaused) return;
    if (!playing || step >= sc.timeline.length - 1) return;
    const t = setTimeout(() => setStep((s) => s + 1), demoActive ? 1500 : 1100);
    return () => clearTimeout(t);
  }, [playing, step, sc, demoActive, demoPaused]);

  useEffect(() => {
    if (!demoActive || demoPaused) return;
    if (step < sc.timeline.length - 1) return;
    if (sc.slaBreach && !breach) return;
    const t = setTimeout(() => {
      if (demoSeqIdx < DEMO_SEQUENCE.length - 1) {
        setDemoSeqIdx(demoSeqIdx + 1);
        setScenarioId(DEMO_SEQUENCE[demoSeqIdx + 1]);
      } else {
        setDemoActive(false);
        setDemoDone(true);
      }
    }, 3000);
    return () => clearTimeout(t);
  }, [demoActive, demoPaused, step, breach, demoSeqIdx, sc]);

  useEffect(() => {
    if (step < 0 || !demoActive) return;
    const e = sc.timeline[step];
    if (!e) return;
    const key = `${sc.id}:${step}`;
    if (pushedSteps.current.has(key)) return;
    pushedSteps.current.add(key);
    setDemoEvents((n) => n + 1);
    setStream((prev) => [{ ts: e.t, system: (e.phase || "EVENT").toUpperCase(), text: `${sc.client.name} — ${e.text}`, scripted: true }, ...prev].slice(0, 14));
    setEventsToday((n) => n + 1);
  }, [step, sc, demoActive]);

  useEffect(() => {
    if (!breach || !demoActive) return;
    if (pushedBreach.current.has(sc.id)) return;
    pushedBreach.current.add(sc.id);
    const entries = sc.response.map((e) => ({ ts: e.t, system: "HUMAN ESCALATION", text: `${sc.client.name} — ${e.text}`, scripted: true }));
    setStream((prev) => [...[...entries].reverse(), ...prev].slice(0, 14));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breach]);

  useEffect(() => {
    if (hasOutcome) applyEffect({ caseId: sc.id, ...(sc.metric || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOutcome, sc]);

  useEffect(() => {
    if (!demoActive || sc.action.kind !== "automated" || !hasAction) return;
    if (demoApplied.current.has(sc.id)) return;
    demoApplied.current.add(sc.id);
    setDemoStats((d) => ({ ...d, automated: d.automated + 1 }));
  }, [demoActive, hasAction, sc]);

  useEffect(() => {
    if (demoActive) return;
    const t = setInterval(() => {
      const now = new Date().toTimeString().slice(0, 8);
      if (feedIdx.current < FEED_SCRIPT.length) {
        const f = FEED_SCRIPT[feedIdx.current];
        feedIdx.current += 1;
        setStream((prev) => [{ ts: f.t, system: f.type, text: `${f.client} — ${f.detail}`, scripted: true }, ...prev].slice(0, 14));
        applyEffect(f.effect);
      } else {
        const a = AMBIENT_EVENTS[ambientIdx.current % AMBIENT_EVENTS.length];
        ambientIdx.current += 1;
        setStream((prev) => [{ ts: now, ...a }, ...prev].slice(0, 14));
      }
      setEventsToday((n) => n + 1);
    }, 3500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoActive]);

  const slaFloor = slaTask ? Math.floor(slaTask.slaMinutes * 60 * (sc.slaStopAt || 0)) : 0;
  const slaActive = !!slaTask && hasAction && !breach && sla !== null && sla > slaFloor;

  useEffect(() => {
    if (!slaActive) return;
    const t = setInterval(() => {
      setSla((s) => {
        if (sc.slaBreach && s <= 13) { setBreach(true); return 0; }
        if (!sc.slaBreach && s <= slaFloor + 13) return slaFloor;
        return s - 13;
      });
    }, demoActive ? 120 : 1000);
    return () => clearInterval(t);
  }, [slaActive, slaFloor, sc, demoActive]);

  const replay = () => {
    feedIdx.current = 0;
    appliedMetrics.current = new Set();
    setMetrics(INITIAL_METRICS);
    setProgressed(new Set());
    setStream([]);
    setStep(-1);
    setPlaying(true);
    setBreach(false);
    setSla(slaTask ? slaTask.slaMinutes * 60 : null);
  };

  const intelMap = useMemo(() => {
    const m = {};
    for (const s of LIVE_SCENARIOS) {
      const st = s.id === sc.id ? step : progressed.has(s.id) ? s.timeline.length - 1 : -1;
      m[s.id] = computeQual(s.id, s.timeline, st, { breach: s.id === sc.id && breach });
    }
    return m;
  }, [sc, step, progressed, breach]);

  const queue = useMemo(() => computeQueue(LIVE_SCENARIOS, intelMap), [intelMap]);
  const selectedRank = queue.find((q) => q.id === sc.id)?.rank;

  return (
    <div className="space-y-6" data-testid="live-page">
      {fromParam === "impact" && (
        <div data-testid="impact-context-banner" className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border border-emerald-500/30 bg-emerald-500/5 rounded-sm px-4 py-2.5">
          <Link
            to="/impact"
            data-testid="back-to-impact-link"
            className="text-[11px] font-mono font-semibold tracking-wider text-emerald-400 hover:text-emerald-300 transition-colors duration-150"
          >
            ← BACK TO IMPACT
          </Link>
          <span className="text-zinc-700 font-mono text-[11px] hidden sm:inline">|</span>
          <span className="text-[11px] font-mono text-zinc-400">
            Viewing intelligence behind this attributed outcome — Impact → Original Case
          </span>
        </div>
      )}
      <DemoHeader
        demoActive={demoActive}
        demoPaused={demoPaused}
        demoSeqIdx={demoSeqIdx}
        seqLength={DEMO_SEQUENCE.length}
        eventsToday={eventsToday}
        onRunDemo={runLiveDemo}
        onTogglePause={() => setDemoPaused(!demoPaused)}
        onNext={nextEvent}
        onRestart={runLiveDemo}
        onReplay={replay}
      />

      {demoDone && <DemoSummary demoEvents={demoEvents} demoStats={demoStats} onRestart={runLiveDemo} />}

      <MetricsStrip metrics={metrics} />
      <SystemsStrip flow={sc.flow} maxChip={maxChip} />
      <PriorityQueue
        scenarios={LIVE_SCENARIOS}
        intelMap={intelMap}
        selectedId={scenarioId}
        onSelect={selectCase}
      />

      <CaseBoard
        scenarios={LIVE_SCENARIOS}
        scenarioId={scenarioId}
        progressed={progressed}
        demoActive={demoActive}
        onSelect={selectCase}
        intel={intelMap}
      />
      {demoActive && <StepTracker trackerIdx={trackerIdx} clientName={sc.client.name} />}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <DetailColumn
          sc={sc}
          phases={phases}
          delivered={delivered}
          opened={opened}
          breach={breach}
          sla={sla}
          slaFloor={slaFloor}
          responseEvents={responseEvents}
          intelligence={<IntelligencePanel sc={sc} step={step} breach={breach} spotlight={intelSpotlight} rank={selectedRank} queueSize={queue.length} />}
        />
        <div className="space-y-4">
          <TimelinePanel timeline={timeline} clientName={sc.client.name} />
          <EventStream stream={stream} />
          <LiveDisclaimer />
        </div>
      </div>
    </div>
  );
}
